import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search } from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";
import { getBaselines } from "../../lib/api";
import { Spinner } from "../../components/ui/spinner";

function formatSize(bytes) {
  if (!bytes) return "-";
  return (bytes / 1048576).toFixed(1) + " MB";
}

function formatPublished(dateStr) {
  if (!dateStr) return "-";
  const d = new Date(dateStr);
  return d.toLocaleDateString("en-GB", { year: "numeric", month: "2-digit", day: "2-digit" }).replace(/\//g, "-")
    + " " + d.toTimeString().slice(0, 5);
}

function shortHash(sha) {
  return sha ? sha.slice(0, 7) + "..." : "-";
}

const STATE_STYLE = {
  SIGNED:     { dot: "bg-blue-500",    pill: "border border-blue-300 text-blue-700 bg-blue-50",         label: "SIGNED" },
  PUBLISHED:  { dot: "bg-emerald-500", pill: "border border-emerald-300 text-emerald-700 bg-emerald-50",   label: "PUBLISHED" },
  DEPRECATED: { dot: null,             pill: "border border-slate-300 text-slate-400 bg-white",          label: "DEPRECATED" },
  DRAFT:      { dot: "bg-slate-400",   pill: "border border-slate-300 text-slate-500 bg-white",          label: "DRAFT" },
  PENDING:    { dot: "bg-amber-400",   pill: "border border-amber-300 text-amber-700 bg-amber-50",       label: "PENDING" },
};

const FILTERS = ["All", "Published", "Signed", "Pending", "Deprecated"];

/* ── Deprecate Confirmation Modal ─────────────────────────────────────── */
function DeprecateModal({ baseline, onConfirm, onCancel }) {
  const { user, role } = useAuth();
  const [reason, setReason] = useState("");
  const [confirmed, setConfirmed] = useState(false);

  const roleLabel = role === "FleetAdmin" ? "Fleet Admin" : role === "ReleaseManager" ? "Fleet Admin" : role || "";
  const name = user?.name || "Unknown";
  const displayName = name.split(" ")[0] + " " + (name.split(" ")[1]?.[0] || "") + ".";
  const now = new Date();
  const timestamp = now.toISOString().slice(0, 10) + " " + now.toTimeString().slice(0, 5) + " UTC";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/40" onClick={onCancel} />

      {/* Modal card */}
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 p-6 space-y-4">

        {/* Title row */}
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-amber-100 flex items-center justify-center text-xl shrink-0">
            &#x26A0;&#xFE0F;
          </div>
          <h2 className="text-lg font-bold text-slate-900">Deprecate {baseline.version}?</h2>
        </div>

        {/* Warning text */}
        <p className="text-sm text-slate-600">
          This action is irreversible. The baseline state will change to{" "}
          <strong>DEPRECATED</strong> and no new devices will be able to receive this
          firmware. This event is immutably audit-logged.
        </p>

        {/* Device impact box */}
        <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3">
          <p className="text-2xl font-bold text-amber-700">{baseline.devicesUpToDate || 0} devices</p>
          <p className="text-xs text-slate-500 mt-0.5">
            currently running {baseline.version} &middot; They will require v1.5.0 (current published baseline)
          </p>
        </div>

        {/* Reason textarea */}
        <div>
          <label className="block text-sm font-semibold text-slate-700 mb-1.5">
            Deprecation Reason <span className="text-red-500">*</span>
          </label>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={3}
            placeholder="Superseded by v1.5.0 (mandatory). Known BLE timeout regression resolved in v1.5.0."
            className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-red-400 resize-none"
          />
        </div>

        {/* Audit log preview */}
        <div className="bg-slate-50 border border-slate-200 rounded-lg px-4 py-3 text-xs text-slate-400 leading-5">
          <span className="font-semibold text-slate-500 block mb-1">Audit log entry will record:</span>
          Actor: {displayName} ({roleLabel}) &middot; Action: DEPRECATE &middot; Version: {baseline.version} &middot; Timestamp: {timestamp}
        </div>

        {/* Confirm button */}
        <button
          disabled={reason.trim().length < 5}
          onClick={() => onConfirm(reason)}
          className="w-full bg-red-600 hover:bg-red-700 disabled:opacity-40 disabled:cursor-not-allowed text-white text-sm font-bold py-3 rounded-xl transition-colors"
        >
          Deprecate {baseline.version} &mdash; I Understand
        </button>

        {/* Cancel button */}
        <button
          onClick={onCancel}
          className="w-full border border-slate-300 text-slate-700 hover:bg-slate-50 text-sm font-semibold py-3 rounded-xl transition-colors"
        >
          Cancel &mdash; Keep {baseline.version} Active
        </button>
      </div>
    </div>
  );
}

/* ── Main Page ────────────────────────────────────────────────────────── */
const PAGE_SIZE = 10;

export default function BaselinesPage() {
  const { canAccess } = useAuth();
  const navigate = useNavigate();
  const [baselines, setBaselines] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, totalPages: 1, total: 0 });
  const [loadError, setLoadError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [activeFilter, setActiveFilter] = useState("All");
  const [deprecateTarget, setDeprecateTarget] = useState(null);

  const fetchPage = (page) => {
    setLoading(true);
    setLoadError(null);
    getBaselines({ page, limit: PAGE_SIZE })
      .then(({ items, total, totalPages }) => {
        setBaselines(items);
        setPagination({ page, totalPages, total });
      })
      .catch((err) => { console.error("[getBaselines] error:", err); setLoadError(err.message); })
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchPage(1); }, []);

  const handleDeprecateConfirm = (reason) => {
    setBaselines((prev) =>
      prev.map((b) =>
        b.id === deprecateTarget.id ? { ...b, state: "DEPRECATED", deprecationReason: reason } : b
      )
    );
    setDeprecateTarget(null);
  };

  const filtered = baselines.filter((b) => {
    const matchState = activeFilter === "All" || b.state.toLowerCase() === activeFilter.toLowerCase();
    const matchSearch = !search || (b.version || "").toLowerCase().includes(search.toLowerCase())
      || (b.releaseNotes || "").toLowerCase().includes(search.toLowerCase());
    return matchState && matchSearch;
  });

  return (
    <div className="flex flex-col h-full">
      {/* Deprecate modal */}
      {deprecateTarget && (
        <DeprecateModal
          baseline={deprecateTarget}
          onConfirm={handleDeprecateConfirm}
          onCancel={() => setDeprecateTarget(null)}
        />
      )}

      {/* Page header */}
      <div className="flex items-start justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Firmware Baselines</h1>
          <p className="text-sm text-slate-400 mt-1">
            All firmware versions &middot; Upload &#8594; Sign &#8594; Publish &#8594; Deprecate lifecycle
          </p>
        </div>
        {canAccess("ReleaseManager", "FleetAdmin") && (
          <button
            onClick={() => navigate("/firmware/upload")}
            className="flex items-center gap-2 bg-teal-800 hover:bg-teal-900 text-white text-sm font-medium px-4 py-2.5 rounded-lg transition-colors"
          >
            + New Baseline
          </button>
        )}
      </div>

      {/* Search + filters */}
      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search version, notes..."
            className="pl-9 pr-4 py-2 text-sm border border-slate-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-teal-500 w-60"
          />
        </div>
        <div className="flex items-center gap-2">
          {FILTERS.map((f) => (
            <button
              key={f}
              onClick={() => setActiveFilter(f)}
              className={`px-4 py-1.5 rounded-full text-sm font-medium border transition-colors ${
                activeFilter === f
                  ? "bg-teal-800 text-white border-teal-800"
                  : "bg-white text-slate-600 border-slate-300 hover:border-teal-400"
              }`}
            >
              {f}
            </button>
          ))}
        </div>
        <span className="ml-auto text-sm text-slate-400">{pagination.total} baselines</span>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-slate-100">
              {["VERSION", "STATE", "TYPE", "SIZE", "SHA-256", "PUBLISHED", "DEVICES", "ACTIONS"].map((h) => (
                <th key={h} className="px-4 py-3 text-left text-[11px] font-semibold text-slate-400 uppercase tracking-wide">
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td colSpan={8} className="px-4 py-10 text-center">
                  <Spinner size="lg" className="mx-auto" />
                </td>
              </tr>
            )}
            {!loading && filtered.length === 0 && (
              <tr>
                <td colSpan={8} className="px-4 py-10 text-center text-slate-400">
                  {loadError ? <span className="text-red-500">Failed to load baselines: {loadError}</span> : "No baselines match your filter."}
                </td>
              </tr>
            )}
            {filtered.map((b) => {
              const st = STATE_STYLE[b.state] || STATE_STYLE.DRAFT;
              const isDeprecated = b.state === "DEPRECATED";
              return (
                <tr
                  key={b.id}
                  onClick={() => navigate(`/firmware/${b.id}`)}
                  className={`border-b border-slate-50 hover:bg-slate-50 cursor-pointer transition-colors ${isDeprecated ? "opacity-60" : ""}`}
                >
                  <td className="px-4 py-3.5 font-semibold text-slate-800">{b.version}</td>

                  <td className="px-4 py-3.5">
                    <span className={`inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full ${st.pill}`}>
                      {st.dot && <span className={`w-1.5 h-1.5 rounded-full ${st.dot}`} />}
                      {st.label}
                    </span>
                  </td>

                  <td className="px-4 py-3.5">
                    {b.mandatory
                      ? <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-red-100 text-red-600 border border-red-200">Mandatory</span>
                      : <span className={`text-xs font-medium px-2.5 py-1 rounded-full border ${isDeprecated ? "border-slate-200 text-slate-400" : "border-teal-300 text-teal-700"}`}>Optional</span>
                    }
                  </td>

                  <td className="px-4 py-3.5 text-slate-600">{formatSize(b.size)}</td>
                  <td className="px-4 py-3.5 font-mono text-slate-500 text-xs">{shortHash(b.sha256)}</td>
                  <td className="px-4 py-3.5 text-slate-500 text-xs whitespace-nowrap">{formatPublished(b.publishedAt)}</td>

                  <td className="px-4 py-3.5">
                    {b.state === "PUBLISHED" && b.devicesUpToDate != null ? (
                      <span className={b.devicesUpToDate < 100 ? "font-semibold text-amber-600" : "font-semibold text-slate-700"}>
                        {b.devicesUpToDate}
                      </span>
                    ) : b.state === "PUBLISHED" ? (
                      <span className="text-slate-500">-</span>
                    ) : (
                      <span className="text-slate-400">{b.devicesUpToDate || 0}</span>
                    )}
                    {b.state === "PUBLISHED" && b.totalDevices && (
                      <span className="text-slate-400"> / {b.totalDevices}</span>
                    )}
                  </td>

                  <td className="px-4 py-3.5" onClick={(e) => e.stopPropagation()}>
                    <div className="flex items-center gap-2">
                      {b.state === "SIGNED" && (
                        <ActionBtn onClick={() => navigate(`/firmware/${b.id}`)}>Publish</ActionBtn>
                      )}
                      {b.state === "PUBLISHED" && (
                        <ActionBtn onClick={() => navigate(`/firmware/${b.id}`)}>Rollout</ActionBtn>
                      )}
                      {b.state === "PUBLISHED" && (
                        <ActionBtn danger onClick={() => setDeprecateTarget(b)}>Deprecate</ActionBtn>
                      )}
                      {b.state === "SIGNED" && (
                        <ActionBtn onClick={() => navigate(`/firmware/${b.id}`)}>View</ActionBtn>
                      )}
                      {b.state === "DEPRECATED" && (
                        <ActionBtn onClick={() => navigate(`/firmware/${b.id}`)}>History</ActionBtn>
                      )}
                      {b.state === "DRAFT" && (
                        <ActionBtn onClick={() => navigate(`/firmware/${b.id}`)}>Review</ActionBtn>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {pagination.totalPages > 1 && (
        <div className="flex flex-col items-center gap-2 mt-5">
          <div className="flex items-center gap-1">
            {/* Previous */}
            <button
              disabled={pagination.page <= 1}
              onClick={() => fetchPage(pagination.page - 1)}
              className="flex items-center gap-1 px-3 py-1.5 text-sm font-medium border border-slate-200 rounded-lg text-slate-600 bg-white hover:bg-slate-50 hover:border-slate-300 disabled:opacity-35 disabled:cursor-not-allowed transition-all shadow-sm"
            >
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" /></svg>
              Prev
            </button>

            {/* Page numbers */}
            {Array.from({ length: pagination.totalPages }, (_, i) => i + 1).map((p) => {
              const current = pagination.page;
              const total = pagination.totalPages;
              // Show first, last, current ±1, and ellipsis
              const show = p === 1 || p === total || Math.abs(p - current) <= 1;
              const ellipsisBefore = p === current - 2 && current - 2 > 1;
              const ellipsisAfter  = p === current + 2 && current + 2 < total;
              if (ellipsisBefore || ellipsisAfter) {
                return <span key={p} className="px-1 text-slate-400 text-sm select-none">…</span>;
              }
              if (!show) return null;
              return (
                <button
                  key={p}
                  onClick={() => fetchPage(p)}
                  className={`min-w-[36px] px-3 py-1.5 text-sm font-medium border rounded-lg transition-all shadow-sm ${
                    p === current
                      ? "bg-teal-800 text-white border-teal-800 shadow-teal-200 shadow"
                      : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50 hover:border-slate-300"
                  }`}
                >
                  {p}
                </button>
              );
            })}

            {/* Next */}
            <button
              disabled={pagination.page >= pagination.totalPages}
              onClick={() => fetchPage(pagination.page + 1)}
              className="flex items-center gap-1 px-3 py-1.5 text-sm font-medium border border-slate-200 rounded-lg text-slate-600 bg-white hover:bg-slate-50 hover:border-slate-300 disabled:opacity-35 disabled:cursor-not-allowed transition-all shadow-sm"
            >
              Next
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" /></svg>
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function ActionBtn({ children, onClick, danger }) {
  return (
    <button
      onClick={onClick}
      className={`text-xs px-3 py-1.5 rounded-md border font-medium transition-colors ${
        danger
          ? "border-red-300 text-red-600 hover:bg-red-50"
          : "border-slate-300 text-slate-600 hover:bg-slate-50"
      }`}
    >
      {children}
    </button>
  );
}